import { useState } from 'react';
import { useApiClient } from '../../../api/client';
import { useToast } from '../../../context/ToastContext';
import { safeJson, fmtDate } from '../../../utils';
import type { ChangeRequest, Approver } from '../../../types';

interface Props { record: ChangeRequest; cid: string; onRefresh: () => Promise<void>; }

export default function RejectionSection({ record, cid, onRefresh }: Props) {
  const api = useApiClient();
  const { showToast } = useToast();
  const [resubmitting, setResubmitting] = useState(false);

  async function resubmit() {
    setResubmitting(true);
    try {
      await api.patch(`/changes/${encodeURIComponent(cid)}/resubmit-plan`, {});
      showToast('Plan returned for revision.', 'success');
      await onRefresh();
    } catch (err) {
      showToast((err as Error).message, 'error');
    } finally {
      setResubmitting(false);
    }
  }

  return (
    <>
      <div className="alert alert--danger" style={{ marginBottom: 16 }}>
        <span className="alert__icon"><i className="fa-solid fa-circle-xmark" aria-hidden="true" /></span>
        <div className="alert__body">
          <div className="alert__title">Rejected by {safeJson<Approver[]>(record.approvers, []).map(a => a.name).join(', ')} on {fmtDate(record.approvalDate)}</div>
          {record.rejectionReason && (
            <div style={{ marginTop: 8, background: 'var(--danger-light)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--radius-sm)', padding: 14 }}>
              <p style={{ color: '#b91c1c', whiteSpace: 'pre-wrap' }}>{record.rejectionReason}</p>
            </div>
          )}
        </div>
      </div>

      <div className="section-panel">
        <div className="section-panel__header">
          <h2 className="section-panel__title"><i className="fa-solid fa-rotate-left" aria-hidden="true" /> Rejection Review</h2>
        </div>
        <div className="section-panel__body">
          <div className="field-display-grid" style={{ marginBottom: 24 }}>
            <div className="field-display"><div className="field-display__label">Change ID</div><div className="field-display__value change-id-mono">{record.changeId}</div></div>
            <div className="field-display"><div className="field-display__label">Change Name</div><div className="field-display__value">{record.changeName}</div></div>
            <div className="field-display"><div className="field-display__label">Scope</div><div className="field-display__value">{record.scope}</div></div>
            <div className="field-display"><div className="field-display__label">Date Submitted</div><div className="field-display__value">{fmtDate(record.dateSubmitted)}</div></div>
          </div>

          <p style={{ marginBottom: 20, color: 'var(--ink-dim)', fontSize: '0.875rem' }}>
            Review the rejection reason above, revise your implementation plan, and resubmit for approval.
          </p>

          <button className={`btn btn--primary${resubmitting ? ' is-loading' : ''}`} onClick={resubmit} disabled={resubmitting}>
            <i className="fa-solid fa-file-pen" aria-hidden="true" /> Revise Implementation Plan
          </button>
        </div>
      </div>
    </>
  );
}
